import { capitalise, isDefined, isNonEmptyString } from 'ts-utils';
import { IbsClassification } from '../businessLogic/AssessmentQuestionTypes';
import { PricingState } from '../store/pricing/PricingState';
import { FlowConfig, ScreenConfig } from './FlowTypes';
import { MenopauseStage, MenopauseType } from './meno/MenoTypes';

export const NoMenopausal = 'I’m not menopausal';

export enum UrlKey {
  REFERRAL_ID = 'referral-id',
  HCP = 'hcp',
  HCP_CLINIC = 'hcp-clinic',
  FREE_TRIAL = 'free-trial',
  COUPON = 'coupon',
  ASL = 'asl',
  UTM_TERM = 'utm_term',
  ENTRY = 'entry',
  UTM_SOURCE = 'utm_source',
  UTM_MEDIUM = 'utm_medium',
  UTM_CAMPAIGN = 'utm_campaign',
  UTM_CONTENT = 'utm_content',
}

export enum ASLClinic {
  Parsley = 'parsley',
  Relish = 'relish',
  LiveWell = 'live-well',
  TinaStorage = 'tina-storage',
  /** Prod test clinic. */
  ProdTest = 'prod-test',
}

export const MENO_SELECTOR_CONFIG = {
  [MenopauseType.Meno]: {
    label: MenopauseType.Meno,
    options: [
      { label: 'Yes', value: MenopauseType.Meno },
      { label: 'No, perimenopausal', value: MenopauseType.Peri },
      { label: 'No, postmenopausal', value: MenopauseType.Post },
      { label: 'I’m not sure', value: 'none' },
    ],
  },
  [MenopauseType.Post]: {
    label: MenopauseType.Post,
    options: [
      { label: 'Yes', value: MenopauseType.Post },
      { label: 'No, perimenopausal', value: MenopauseType.Peri },
      { label: 'No, menopausal', value: MenopauseType.Meno },
      { label: 'I’m not sure', value: 'none' },
    ],
  },
  [MenopauseType.Peri]: {
    label: MenopauseType.Peri,
    options: [
      { label: 'Yes', value: MenopauseType.Peri },
      { label: 'No, menopausal', value: MenopauseType.Meno },
      { label: 'No, postmenopausal', value: MenopauseType.Post },
      { label: 'I’m not sure', value: 'none' },
    ],
  },
  [MenopauseType.Regular]: {
    label: MenopauseType.Peri,
    options: [
      { label: 'Perimenopause', value: MenopauseType.Peri },
      { label: 'Menopause', value: MenopauseType.Meno },
      { label: 'Postmenopause', value: MenopauseType.Post },
      { label: NoMenopausal, value: NoMenopausal },
      { label: 'I’m not sure', value: 'none' },
    ],
  },
};

export const NERVA_SELECTOR_CONFIG = {
  [IbsClassification.IBS_M]: {
    options: [
      { label: 'Yes', value: IbsClassification.IBS_M },
      { label: 'No, IBS-C (Constipation)', value: IbsClassification.IBS_C },
      { label: 'No, IBS-D (Diarrhea)', value: IbsClassification.IBS_D },
      { label: 'I’m not sure', value: 'none' },
    ],
  },
  [IbsClassification.IBS_C]: {
    options: [
      { label: 'Yes', value: IbsClassification.IBS_C },
      { label: 'No, IBS-D (Diarrhea)', value: IbsClassification.IBS_D },
      { label: 'No, IBS-M (Mixed)', value: IbsClassification.IBS_M },
      { label: 'I’m not sure', value: 'none' },
    ],
  },
  [IbsClassification.IBS_D]: {
    options: [
      { label: 'Yes', value: IbsClassification.IBS_D },
      { label: 'No, IBS-C (Constipation)', value: IbsClassification.IBS_C },
      { label: 'No, IBS-M (Mixed)', value: IbsClassification.IBS_M },
      { label: 'I’m not sure', value: 'none' },
    ],
  },
};

export const NERVA_SELECTOR_AI_PERSONALISED_CONFIG = {
  [IbsClassification.IBS_M]: {
    options: [
      { label: 'Yes', value: IbsClassification.IBS_M },
      { label: 'I’m not sure', value: 'none' },
      { label: 'No, IBS-C (Constipation)', value: IbsClassification.IBS_C },
      { label: 'No, IBS-D (Diarrhea)', value: IbsClassification.IBS_D },
    ],
  },
  [IbsClassification.IBS_C]: {
    options: [
      { label: 'Yes', value: IbsClassification.IBS_C },
      { label: 'I’m not sure', value: 'none' },
      { label: 'No, IBS-D (Diarrhea)', value: IbsClassification.IBS_D },
      { label: 'No, IBS-M (Mixed)', value: IbsClassification.IBS_M },
    ],
  },
  [IbsClassification.IBS_D]: {
    options: [
      { label: 'Yes', value: IbsClassification.IBS_D },
      { label: 'I’m not sure', value: 'none' },
      { label: 'No, IBS-C (Constipation)', value: IbsClassification.IBS_C },
      { label: 'No, IBS-M (Mixed)', value: IbsClassification.IBS_M },
    ],
  },
};

export function menopauseTypeFromStage(stage: string): MenopauseType {
  switch (stage) {
    case MenopauseStage.PastYear:
      return MenopauseType.Meno;
    case MenopauseStage.YearAgo:
      return MenopauseType.Post;
    case MenopauseStage.Regular:
      return MenopauseType.Regular;
    default:
      return MenopauseType.Peri;
  }
}

export function ibsTypeFromSymptoms(symptoms: string[]): IbsClassification {
  if (symptoms.includes('Constipation') && !symptoms.includes('Diarrhea')) {
    return IbsClassification.IBS_C;
  } else if (!symptoms.includes('Constipation') && symptoms.includes('Diarrhea')) {
    return IbsClassification.IBS_D;
  } else {
    return IbsClassification.IBS_M;
  }
}

/**
 * This function takes two FlowConfigs and merges them together.
 * If a screen is present in both configs, the screen from the experimentOverlay will be used.
 * If a screen is present in only one config, that screen will be included in the result.
 *
 * __Note__: This function does not handle the case where you want to remove a screen during an experiment.
 * If you want to remove a screen during an experiment, you must include a screen that directly references
 * the screen you want to remove and set the `nextScreen` property to an alternative screen.
 *
 * @example extending a config with one experiment
 * ```ts
 * import {NERVA_CONFIG} from './NervaConfig';
 * import {NERVA_EXPERIMENT_CONFIG} from './NervaExperimentConfig';
 *
 * const nervaConfig = Experiments.YourExperiment.select({
 * 	'Control': NERVA_CONFIG,
 * 	'YourExperiment': assembleExperimentConfiguration(NERVA_CONFIG, NERVA_EXPERIMENT_CONFIG),
 * })
 * ```
 *
 * @param baseConfig - The base config to start with
 * @param experimentOverlay - The experiment config to overlay on top of the base config
 */
export function assembleExperimentConfiguration<
  BaseScreenName extends string,
  OverlayScreenName extends string,
>(
  baseConfig: FlowConfig<BaseScreenName>,
  experimentOverlay: FlowConfig<OverlayScreenName>
): FlowConfig<BaseScreenName | OverlayScreenName> {
  const name = `${baseConfig.name}_${experimentOverlay.name}`;

  // Create a map of screen names to screen configs
  // E.g. { 'Screen1': { ... }, 'Screen2': { ... } }
  const screensMap = new Map<
    BaseScreenName | OverlayScreenName,
    ScreenConfig<BaseScreenName | OverlayScreenName>
  >(baseConfig.screens.map((screen) => [screen.screenName, screen]));

  // Add the screens from the experimentOverlay to the map
  // This will overwrite any screens that are present in both configs
  // And add any screens that are only present in the experimentOverlay
  experimentOverlay.screens.forEach((overlayScreen) =>
    screensMap.set(overlayScreen.screenName, overlayScreen)
  );

  return { ...baseConfig, name, screens: Array.from(screensMap.values()) };
}

/**
 * This function takes a FlowConfig and returns an array of all the screen names in the config.
 */
export function getAllScreens(config: FlowConfig<string>): string[] {
  return config.screens.map((screen) => screen.screenName);
}

/**
 * This function takes a FlowConfig and returns an array of all the screen names in the config with numbering
 * based on their position in the config.
 * __Note:__ This function is not representative of the actual screen flow as it does not take into account
 * the `nextScreen` property of each screen and instead just returns the screens in the order they are defined
 */
export function getAllScreensWithNumbering(
  config: FlowConfig<string>
): Array<{ step: number; screen: string }> {
  return config.screens.map((screen, index) => ({
    step: index + 1,
    screen: screen.screenName,
  }));
}

function getUrlParams(key: UrlKey): string | undefined {
  const urlParams = new URLSearchParams(window.location.search);
  const value = urlParams.get(key);
  return isDefined(value) ? value : undefined;
}

export function hasReferralInURL(): boolean {
  return isNonEmptyString(getReferralIdSearchParam());
}

export function getReferralIdSearchParam(): string | undefined {
  // we check if the user has a referral id as a query string parameter.
  return getUrlParams(UrlKey.REFERRAL_ID);
}

/** `entry` URL param value used by 65+ re-engagement links to land on the Medicare-type screen. */
export const MEDICARE_ACCESS_ENTRY = 'medicare-access';

export function hasMedicareAccessEntryInURL(): boolean {
  return getUrlParams(UrlKey.ENTRY) === MEDICARE_ACCESS_ENTRY;
}

// Referrals created in hermes before it was decommissioned were not 20 characters, they were the id value from the postgres primary key column in referrals table.
function isPostgresPrimaryKeyFormat(referralId: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(referralId);
}

// Tabasco referrals.id are `referral_` prefix with a UUIDv4 suffix, e.g. `referral_123e4567-e89b-12d3-a456-426614174000`
export function isTabascoReferralIdFormat(referralId: string): boolean {
  const tabascoReferralRegex =
    /^referral_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return tabascoReferralRegex.test(referralId);
}

// If query string referral ID is not a 20 character string, we don't need to query hcp_referrals to know it does not exist
export function isCorrectFormatForReferralId(referralId: string | undefined): referralId is string {
  if (!isDefined(referralId)) return false;
  if (referralId.length === 20) return true;
  if (isPostgresPrimaryKeyFormat(referralId)) return true;
  if (isTabascoReferralIdFormat(referralId)) return true;
  return false;
}

export function hasASLInURL(): boolean {
  return isNonEmptyString(getASLClinicSearchParam());
}

export function getASLClinicSearchParam(): string | undefined {
  // we check if the user has a referral id as a query string parameter.
  return getUrlParams(UrlKey.ASL);
}

export function getFreeTrialSearchParamIsTrue(): boolean {
  return getUrlParams(UrlKey.FREE_TRIAL) === 'true';
}

export function getUtmTermSearchParam(): string | undefined {
  return getUrlParams(UrlKey.UTM_TERM);
}

export function getUtmSourceSearchParam(): string | undefined {
  return getUrlParams(UrlKey.UTM_SOURCE);
}

export function getUtmMediumSearchParam(): string | undefined {
  return getUrlParams(UrlKey.UTM_MEDIUM);
}

export function getUtmCampaignSearchParam(): string | undefined {
  return getUrlParams(UrlKey.UTM_CAMPAIGN);
}

export function getUtmContentSearchParam(): string | undefined {
  return getUrlParams(UrlKey.UTM_CONTENT);
}

/** All UTM params from the current URL, shaped for the checkout / grant API calls. */
export function getUtmParams(): {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
} {
  return {
    utmSource: getUtmSourceSearchParam(),
    utmMedium: getUtmMediumSearchParam(),
    utmCampaign: getUtmCampaignSearchParam(),
    utmTerm: getUtmTermSearchParam(),
    utmContent: getUtmContentSearchParam(),
  };
}

export function getDiscountForReferralCoupon(): string | undefined {
  const couponToDiscountMap: Record<string, string> = {
    ZJ4LQ50: '50%',
    VGZK25: '25%',
  };

  const coupon = couponCodeFromQueryParam();
  return isDefined(coupon) ? couponToDiscountMap[coupon] : undefined;
}

/** Get the `couponCode` from the `coupon` query param. */
export function couponCodeFromQueryParam(): PricingState['couponCode'] {
  return getUrlParams(UrlKey.COUPON);
}

export function getHcpDetailsFromUrl(): { hcp: string; hcpClinic: string } | undefined {
  const hcp = getUrlParams(UrlKey.HCP);
  const hcpClinic = getUrlParams(UrlKey.HCP_CLINIC);
  const areHcpDetailsDefined = isDefined(hcp) && isDefined(hcpClinic);

  if (areHcpDetailsDefined) {
    return { hcp, hcpClinic };
  }
  return undefined;
}

/** Formats HCP name and clinic as "Name at Clinic" in title case, or returns a fallback string if missing. */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function formatHcpDetails(hcpDetails: ReturnType<typeof getHcpDetailsFromUrl> | undefined) {
  return isDefined(hcpDetails)
    ? (`${capitalise(hcpDetails.hcp.toLowerCase())} at ${capitalise(hcpDetails.hcpClinic.toLowerCase())}` as const)
    : ('your healthcare professional' as const);
}
